/**
 * Configuration service for managing application settings.
 * 
 * This service handles configuration management including remote API URLs,
 * environment-specific settings, and runtime configuration options.
 */

/**
 * Application configuration interface.
 * 
 * Defines the structure for application configuration including
 * API endpoints, feature flags, and other runtime settings.
 */
export interface AppConfig {
  /** Remote API base URL for availabilities endpoint */
  remoteApiUrl?: string;
  /** Provider ID for API calls */
  providerId?: string;
  /** Environment name (development, staging, production) */
  environment?: string;
  /** External service URLs */
  external?: {
    /** Provider image API URL */
    providerImageUrl?: string;
    /** Header HTML API URL */
    headerUrl?: string;
    /** Footer HTML API URL */
    footerUrl?: string;
  };
  /** Feature flags for enabling/disabling functionality */
  features?: {
    useRemoteApi?: boolean;
    enableDebugLogging?: boolean;
  };
}

/**
 * Default configuration values.
 */
const DEFAULT_CONFIG: Required<AppConfig> = {
  remoteApiUrl: '',
  providerId: '', // No default - must come from contactId param or session
  environment: 'development',
  external: {
    providerImageUrl: 'https://mindfultherapygroup.com/wp-json/provider/v1/headshot/',
    headerUrl: 'https://mindfultherapygroup.com/wp-json/custom/v1/header',
    footerUrl: 'https://mindfultherapygroup.com/wp-json/custom/v1/footer'
  },
  features: {
    useRemoteApi: false,
    enableDebugLogging: false
  }
};

/**
 * Global configuration state.
 */
let currentConfig: AppConfig = { ...DEFAULT_CONFIG };

/**
 * Get the current application configuration.
 * 
 * Returns a copy of the current configuration to prevent external modification.
 * 
 * @returns Current application configuration
 * 
 * @example
 * ```typescript
 * const config = getConfig();
 * 
 * ```
 */
export function getConfig(): AppConfig {
  return { ...currentConfig };
}

/**
 * Update the application configuration.
 * 
 * Merges the provided configuration with the existing configuration,
 * allowing partial updates while preserving existing values.
 * 
 * @param newConfig - Partial configuration to merge
 * @returns Updated configuration
 * 
 * @example
 * ```typescript
 * setConfig({
 *   remoteApiUrl: 'https://api.example.com',
 *   features: { useRemoteApi: true }
 * });
 * ```
 */
export function setConfig(newConfig: Partial<AppConfig>): AppConfig {
  currentConfig = {
    ...currentConfig,
    ...newConfig,
    external: {
      ...currentConfig.external,
      ...newConfig.external
    },
    features: {
      ...currentConfig.features,
      ...newConfig.features
    }
  };
  
  if (currentConfig.features?.enableDebugLogging) {
  }
  
  return getConfig();
}

/**
 * Reset configuration to default values.
 * 
 * Restores the configuration to the default state, clearing any
 * custom settings that may have been applied.
 * 
 * @returns Reset configuration
 * 
 * @example
 * ```typescript
 * resetConfig();
 * ```
 */
export function resetConfig(): AppConfig {
  currentConfig = { ...DEFAULT_CONFIG };
  
  if (currentConfig.features?.enableDebugLogging) {
  }
  
  return getConfig();
}

/**
 * Initialize configuration from environment variables or URL parameters.
 * 
 * This function attempts to load configuration from various sources:
 * 1. Environment variables (REACT_APP_*)
 * 2. URL search parameters
 * 3. Local storage
 * 4. Development defaults (if in development mode)
 * 
 * @returns Loaded configuration
 * 
 * @example
 * ```typescript
 * const config = await initializeConfig();
 * ```
 */
export async function initializeConfig(): Promise<AppConfig> {
  const config: Partial<AppConfig> = {};
  
  // Clear old localStorage config since we're using backend session saving now
  if (typeof window !== 'undefined') {
    localStorage.removeItem('app-config');
  }
  
  
    // Load from environment variables
    if (typeof window !== 'undefined') {
      // Browser environment
      const envRemoteUrl = process.env.REACT_APP_REMOTE_API_URL;
      const envProviderId = process.env.REACT_APP_PROVIDER_ID;
      const envEnvironment = process.env.REACT_APP_ENVIRONMENT;
      const envProviderImageUrl = process.env.REACT_APP_PROVIDER_IMAGE_URL;
      const envHeaderUrl = process.env.REACT_APP_HEADER_URL;
      const envFooterUrl = process.env.REACT_APP_FOOTER_URL;
      
      // Debug environment variables
      
    
    if (envRemoteUrl) config.remoteApiUrl = envRemoteUrl;
    if (envProviderId) config.providerId = envProviderId;
    if (envEnvironment) config.environment = envEnvironment;
    if (envProviderImageUrl || envHeaderUrl || envFooterUrl) {
      config.external = {
        ...config.external,
        ...(envProviderImageUrl && { providerImageUrl: envProviderImageUrl }),
        ...(envHeaderUrl && { headerUrl: envHeaderUrl }),
        ...(envFooterUrl && { footerUrl: envFooterUrl })
      };
    }
    
    // Load from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const urlRemoteUrl = urlParams.get('remoteApiUrl');
    const urlProviderId = urlParams.get('providerId');
    
    if (urlRemoteUrl) config.remoteApiUrl = urlRemoteUrl;
    if (urlProviderId) config.providerId = urlProviderId;
    
    
    // Default to production API if no remote API URL is set
    if (!config.remoteApiUrl) {
      config.remoteApiUrl = 'https://scheduling-api.mindfultherapygroup.com/';
      config.features = {
        ...config.features,
        useRemoteApi: true,
        enableDebugLogging: true
      };
    }
  }
  
  // Apply configuration
  const finalConfig = setConfig(config);
  
  return finalConfig;
}

/**
 * Check if remote API is configured and enabled.
 * 
 * @returns True if remote API should be used, false otherwise
 * 
 * @example
 * ```typescript
 * if (isRemoteApiEnabled()) {
 *   // Use remote API
 * } else {
 *   // Show error - API is required
 * }
 * ```
 */
export function isRemoteApiEnabled(): boolean {
  const config = getConfig();
  return !!(config.remoteApiUrl && config.remoteApiUrl.trim() !== '');
}

/**
 * Get the full API endpoint URL for availabilities.
 * 
 * Constructs the complete URL for the availabilities API endpoint
 * using the current configuration.
 * 
 * @returns Full API endpoint URL or null if not configured
 * 
 * @example
 * ```typescript
 * const apiUrl = getAvailabilitiesApiUrl();
 * if (apiUrl) {
 *   // Make API call
 * }
 * ```
 */
export function getAvailabilitiesApiUrl(): string | null {
  const config = getConfig();
  
  if (!config.remoteApiUrl || config.remoteApiUrl.trim() === '') {
    return null;
  }
  
  const baseUrl = config.remoteApiUrl.replace(/\/$/, ''); // Remove trailing slash
  return `${baseUrl}/api/IoiApi/providers/${config.providerId}/availabilities`;
}

/**
 * Get the provider image API URL.
 * 
 * Returns the configured provider image API URL for fetching provider headshots.
 * 
 * @returns Provider image API URL
 * 
 * @example
 * ```typescript
 * const imageUrl = getProviderImageUrl();
 * const response = await fetch(`${imageUrl}${providerCode}`);
 * ```
 */
export function getProviderImageUrl(): string {
  const config = getConfig();
  return config.external?.providerImageUrl || 'https://mindfultherapygroup.com/wp-json/provider/v1/headshot/';
}

/**
 * Get the configured header HTML API URL.
 *
 * @returns Header API URL
 */
export function getHeaderUrl(): string {
  const config = getConfig();
  return config.external?.headerUrl || 'https://scheduling-api.mindfultherapygroup.com/api/static/Header';
}

/**
 * Get the configured footer HTML API URL.
 *
 * @returns Footer API URL
 */
export function getFooterUrl(): string {
  const config = getConfig();
  return config.external?.footerUrl || 'https://scheduling-api.mindfultherapygroup.com/api/static/Footer';
}

/** Default origin for header/footer assets (stylesheet, etc.) when no header URL is configured. */
const DEFAULT_HEADER_FOOTER_ORIGIN = 'https://scheduling-api.mindfultherapygroup.com/api/static/';

/** Path suffix for the theme stylesheet, used with header/footer origin. */
export const HEADER_FOOTER_STYLESHEET_PATH = '/api/static/wp-content/themes/mindful-therapy-group/css/style.css?ver=2.1.9';

/**
 * Get the origin (scheme + host) to use for header/footer assets (e.g. stylesheet).
 * Env vars override so sandbox gets scheduling-sandbox-api... for stylesheet injection.
 *
 * @returns Origin URL (e.g. https://scheduling-sandbox-api.mindfultherapygroup.com)
 */
export function getHeaderFooterOrigin(): string {
  const envHeader = process.env.REACT_APP_HEADER_URL;
  if (envHeader) {
    try {
      return new URL(envHeader).origin;
    } catch { /* fall through */ }
  }
  const headerUrl = getHeaderUrl();
  try {
    return new URL(headerUrl).origin;
  } catch {
    return DEFAULT_HEADER_FOOTER_ORIGIN;
  }
}

/** Origins that should be rewritten to the current header/footer origin (e.g. so sandbox gets sandbox assets). */
const HEADER_FOOTER_ASSET_ORIGINS = [
  'https://mindfultherapygroup.com',
  'https://mindfultherdev.wpenginepowered.com',
  'https://scheduling-api.mindfultherapygroup.com',
  'https://scheduling-sandbox-api.mindfultherapygroup.com'
];

/** Path prefix for static assets served by the backend API (e.g. /api/static/wp-includes/...). */
const STATIC_API_PATH_PREFIX = '/api/static';

/**
 * Rewrite a stylesheet (or other asset) href so it uses the current environment's origin
 * and routes through the backend static API (/api/static/...).
 *
 * @param href - Original href (relative or absolute)
 * @returns URL that uses the configured header/footer origin and /api/static path prefix
 */
export function rewriteHeaderFooterAssetUrl(href: string): string {
  if (!href || !href.trim()) return href;
  const origin = getHeaderFooterOrigin();
  const trimmed = href.trim();
  if (!trimmed.startsWith('http') && !trimmed.startsWith('//')) {
    try {
      if (trimmed.startsWith('/')) {
        return `${origin}${STATIC_API_PATH_PREFIX}${trimmed}`;
      }
      return new URL(trimmed, `${origin}${STATIC_API_PATH_PREFIX}/`).href;
    } catch {
      return href;
    }
  }
  try {
    const url = new URL(trimmed);
    if (HEADER_FOOTER_ASSET_ORIGINS.some((o) => url.origin === o)) {
      return `${origin}${STATIC_API_PATH_PREFIX}${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    // ignore
  }
  return href;
}
